/**
 * App — the React Router v6 router (the workspace ships v6.28; v7 data-router
 * adoption is tracked separately).
 *
 * Route structure mirrors FRONTEND_PLAN.md §5 (S3 row): seven side-nav
 * destinations plus `/onboarding`, `/login`, and the `/slack/callback`
 * landing route. Every protected route is rendered inside `AppShell`
 * via a router `Outlet`, so the layout shell mounts once per session and
 * survives client-side navigation.
 *
 * `/play` is the v1 default: the Play surface is what `/` redirects to and
 * what the side-nav home points at. `/home` is reserved for an
 * eventual landing-page surface that S7 may extend.
 *
 * `/onboarding` is rendered outside the app shell (no side nav, no status
 * bar) — it is a focused first-run wizard, owned by S4.
 *
 * `/slack/callback` is a public route. The Slack OAuth popup or same-tab
 * redirect lands here with `?code=…&state=…`; the page finalises the bind
 * via POST /api/slack/oauth/callback and either posts a message back to
 * the SPA opener or redirects to /settings.
 *
 * The four S7 data-UI pages (Connections, Memory, Settings, Tasks) pull in
 * heavy Cloudscape primitives (Table, Tabs, Container, Modal). Lazy-load
 * them per route to keep the initial bundle under the 250 KB gz budget
 * locked in `WayneAIPlatformFrontend/CLAUDE.md`.
 */
import { lazy, Suspense } from 'react';
import { Route, BrowserRouter, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FlagsProvider } from './contexts/FlagsContext';
import { WsBridgeProvider } from './services/ws/WsBridgeProvider';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './components/auth/LoginPage';
import AppShell from './components/shell/AppShell';
import Play from './pages/Play';
import Home from './pages/Home';
import Onboarding from './pages/Onboarding';
import SlackCallback from './pages/SlackCallback';
import { NotFoundPage } from './components/organisms/ErrorStates';
import { createQueryClient } from './api/queryClient';

/**
 * Retry wrapper for lazy imports. When a chunk fails to load (e.g., after a
 * deployment invalidates old hashes, or a Vite dep re-optimization race), retry
 * up to 2 times with a small delay. On final failure, reload the page so the
 * browser fetches the updated index.html with correct chunk references.
 */
function lazyRetry<T extends { default: React.ComponentType<unknown> }>(
  importFn: () => Promise<T>,
): React.LazyExoticComponent<T['default']> {
  return lazy(async () => {
    const MAX_RETRIES = 2;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        return await importFn();
      } catch (err) {
        if (attempt === MAX_RETRIES) {
          // Final attempt failed — reload the page so index.html is re-fetched.
          // Guard against infinite reload loops with sessionStorage.
          const reloadKey = 'wayne-chunk-reload';
          if (!sessionStorage.getItem(reloadKey)) {
            sessionStorage.setItem(reloadKey, '1');
            window.location.reload();
          }
          throw err;
        }
        // Wait before retrying (100ms, then 200ms)
        await new Promise((r) => setTimeout(r, (attempt + 1) * 100));
      }
    }
    // Unreachable, but satisfies TypeScript
    throw new Error('Failed to load module');
  });
}

const Connections = lazyRetry(() => import('./pages/Connections'));
const Memory = lazyRetry(() => import('./pages/Memory'));
const Settings = lazyRetry(() => import('./pages/Settings'));
const Tasks = lazyRetry(() => import('./pages/Tasks'));
const Workflows = lazyRetry(() => import('./pages/Workflows'));
const PlaybookDetail = lazyRetry(() => import('./pages/PlaybookDetail'));
const WorkflowTemplates = lazyRetry(() => import('./pages/WorkflowTemplates'));
const WorkflowCreate = lazyRetry(() => import('./pages/WorkflowCreate'));
const WorkflowExecution = lazyRetry(() => import('./pages/WorkflowExecution'));
const WorkflowBuilder = lazyRetry(() => import('./pages/WorkflowBuilder'));
const SlackIntegration = lazyRetry(() => import('./pages/SlackIntegration'));

// Single QueryClient per app boot. The factory also wires the API-client
// interceptors (CSRF + 401 redirect) before any query runs.
const queryClient = createQueryClient();

const App = (): JSX.Element => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FlagsProvider>
          <WsBridgeProvider>
          <BrowserRouter>
            <ErrorBoundary boundaryName="Top">
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/slack/callback" element={<SlackCallback />} />
                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute>
                        <Onboarding />
                      </ProtectedRoute>
                    }
                  />
                  {/*
                    Post-Cloudscape rebuild (ADR-001): every protected route
                    renders through the custom `AppShell`. The Cloudscape-era
                    `WayneAppLayout` and its dependent organisms have been
                    removed.
                  */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <AppShell />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/" element={<Home />} />
                    <Route path="/home" element={<Home />} />
                    <Route path="/play" element={<Play />} />
                    <Route path="/tasks" element={<Tasks />} />
                    <Route path="/connections" element={<Connections />} />
                    <Route path="/memory" element={<Memory />} />
                    <Route path="/workflows" element={<Workflows />} />
                    <Route path="/workflows/playbook/:id" element={<PlaybookDetail />} />
                    <Route path="/workflows/templates" element={<WorkflowTemplates />} />
                    <Route path="/workflows/create" element={<WorkflowCreate />} />
                    <Route path="/workflows/execution/:id" element={<WorkflowExecution />} />
                    <Route path="/workflows/builder" element={<WorkflowBuilder />} />
                    <Route path="/workflows/builder/:id" element={<WorkflowBuilder />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/slack" element={<SlackIntegration />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
          </WsBridgeProvider>
        </FlagsProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
